import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Table,
  Tabs,
  Card,
  Space,
  Tag,
  Tooltip,
  Badge,
  Statistic,
  Row,
  Col,
  Input,
  Collapse
} from "antd";
import {
  PlusOutlined,
  ExclamationCircleFilled,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  FolderOutlined,
  SearchOutlined,
  ReloadOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import AddCategorie from "./AddCategorie";
import AddBranche from "./AddBranche";
import ActionDropdown from "../../production/components/ActionDropdown";
import axiosInstance from "../../../variables/api/axiosInstance";

const { confirm } = Modal;
const { Search } = Input;

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showCategorieModal, setShowCategorieModal] = useState(false);
  const [showBrancheModal, setShowBrancheModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCategorie, setSelectedCategorie] = useState(null);
  const [selectedBranche, setSelectedBranche] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const refreshCategories = (addedOrUpdatedId = null) => {
    axiosInstance
      .get(`/api/categorie`)
      .then((res) => {
        let updatedCategories = res.data;
        if (addedOrUpdatedId) {
          const itemIndex = updatedCategories.findIndex(
            (cat) => cat.categorieId === addedOrUpdatedId
          );
          if (itemIndex > -1) {
            const [movedItem] = updatedCategories.splice(itemIndex, 1);
            updatedCategories.unshift(movedItem);
          }
        } else {
          updatedCategories.sort((a, b) => b.categorieId - a.categorieId);
        }
        setCategories(updatedCategories);
      })
      .catch(() => toast.error("Erreur lors du chargement des catégories"));
  };

  const refreshBranches = () => {
    axiosInstance
      .get("/api/branche")
      .then((res) => setBranches(res.data))
      .catch(() => toast.error("Erreur lors du chargement des branches"));
  };

  useEffect(() => {
    refreshCategories();
    refreshBranches();
  }, []);

  const showDeleteConfirm = (categorieId) => {
    confirm({
      title: "Êtes-vous sûr de vouloir supprimer cette catégorie ?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        axiosInstance
          .delete(`/api/categorie/${categorieId}`)
          .then(() => {
            toast.success("Catégorie supprimée avec succès");
            refreshCategories();
          })
          .catch(() => toast.error("Erreur lors de la suppression"));
      },
    });
  };

  const showDeleteBrancheConfirm = (brancheId) => {
    const hasCategories = categories.some(
      (cat) => cat.branche?.brancheId === brancheId
    );
    if (hasCategories) {
      toast.error("Impossible de supprimer une branche contenant des catégories");
      return;
    }

    confirm({
      title: "Êtes-vous sûr de vouloir supprimer cette branche ?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        axiosInstance
          .delete(`/api/branche/${brancheId}`)
          .then(() => {
            toast.success("Branche supprimée avec succès");
            refreshBranches();
          })
          .catch(() => toast.error("Erreur lors de la suppression"));
      },
    });
  };

  const handleShowCategorie = (edit, categorieId = null) => {
    setEditMode(edit);
    if (edit) {
      const categorie = categories.find((cat) => cat.categorieId === categorieId);
      setSelectedCategorie(categorie || null);
    } else {
      setSelectedCategorie(null);
    }
    setShowCategorieModal(true);
  };

  const handleShowBranche = (edit, brancheId = null) => {
    setEditMode(edit);
    if (edit) {
      const branche = branches.find((b) => b.brancheId === brancheId);
      setSelectedBranche(branche || null);
    } else {
      setSelectedBranche(null);
    }
    setShowBrancheModal(true);
  };

  const handleCloseBranche = () => {
    setShowBrancheModal(false);
    refreshBranches();
  };

  const handleCloseCategorie = (affectedCategoryId = null) => {
    setShowCategorieModal(false);
    if (affectedCategoryId) {
      refreshCategories(affectedCategoryId);
    } else {
      refreshCategories();
    }
  };

  const columns = [
    {
      title: "Nom",
      dataIndex: "nom",
      key: "nom",
      sorter: (a, b) => a.nom.localeCompare(b.nom),
      filteredValue: [searchText],
      onFilter: (value, record) =>
        record.nom.toLowerCase().includes(value.toLowerCase()) ||
        record.codeCategorie.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Code",
      dataIndex: "codeCategorie",
      key: "codeCategorie",
      render: (code) => <Tag color="blue">{code}</Tag>,
      sorter: (a, b) => a.codeCategorie.localeCompare(b.codeCategorie),
    },
    {
      title: "Branche",
      dataIndex: "branche",
      key: "branche",
      render: (branche) => (
        <Tag color="purple" icon={<FolderOutlined />}>
          {branche?.nom || "—"}
        </Tag>
      ),
      sorter: (a, b) => a.branche?.nom.localeCompare(b.branche?.nom),
    },
    {
      title: "Valeur vénale min",
      dataIndex: "valeurVenaleMinimale",
      key: "valeurVenaleMinimale",
      render: (valeur) => (
        <span style={{ fontWeight: 600 }}>
          {valeur ? new Intl.NumberFormat("fr-FR").format(valeur) + " FCFA" : "—"}
        </span>
      ),
      sorter: (a, b) => a.valeurVenaleMinimale - b.valeurVenaleMinimale,
      align: "right",
    },
    {
      title: "Places",
      key: "places",
      render: (_, record) => {
        if (record.nombrePlaceMin || record.nombrePlaceMax) {
          return (
            <Tooltip
              title={`Min: ${record.nombrePlaceMin || "-"}, Max: ${record.nombrePlaceMax || "-"
                }, Défaut: ${record.nombrePlaceParDefaut || "-"}`}
            >
              <Tag color="green">
                {record.nombrePlaceMin || "?"} - {record.nombrePlaceMax || "?"}
              </Tag>
            </Tooltip>
          );
        }
        return "—";
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <ActionDropdown
          actions={[
            {
              key: "edit",
              label: "Modifier",
              icon: <EditOutlined />,
              onClick: () => handleShowCategorie(true, record.categorieId),
            },
            {
              key: "delete",
              label: "Supprimer",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => showDeleteConfirm(record.categorieId),
            },
          ]}
        />
      ),
    },
  ];

  const categoriesByBranche = branches.map((branche) => ({
    ...branche,
    categories: categories.filter((cat) => cat.branche?.brancheId === branche.brancheId),
  }));

  const categoriesSansBranche = categories.filter((cat) => !cat.branche);

  const calculerStats = () => {
    return {
      totalCategories: categories.length,
      totalBranches: branches.length,
      moyenneParBranche:
        branches.length > 0 ? (categories.length / branches.length).toFixed(1) : 0,
    };
  };

  const stats = calculerStats();

  const renderBrancheCard = (branche) => (
    <Card
      key={branche.brancheId}
      title={
        <Space>
          <FolderOutlined style={{ color: "#722ed1" }} />
          <span style={{ fontWeight: 600 }}>{branche.nom}</span>
          <Tag color="purple">{branche.codeBranche}</Tag>
          <Badge count={branche.categories.length} style={{ backgroundColor: "#52c41a" }} />
        </Space>
      }
      extra={
        <Space>
          <Tooltip title="Modifier la branche">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleShowBranche(true, branche.brancheId)}
            />
          </Tooltip>
          <Tooltip title="Supprimer la branche">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteBrancheConfirm(branche.brancheId)}
            />
          </Tooltip>
        </Space>
      }
      style={{
        marginBottom: 16,
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      {branche.description && (
        <p style={{ color: "#666", marginBottom: 16, fontStyle: "italic" }}>
          {branche.description}
        </p>
      )}
      <Table
        columns={columns}
        dataSource={branche.categories}
        rowKey="categorieId"
        pagination={false}
        size="small"
      />
    </Card>
  );

  return (
    <div className="layout-wrapper layout-content-navbar">
      <style>{`
        .categories-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 16px;
          padding: 32px;
          margin-bottom: 24px;
          color: white;
          box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
        }
        .categories-header h2 {
          color: white;
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 8px 0;
        }
        .stats-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .action-bar {
          background: white;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
      `}</style>

      <div className="layout-container">
        <SideBar currentMenu={"TARIFICATION"} />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper" style={{ background: "#f5f5f5" }}>
            <div className="container-xxl flex-grow-1 container-p-y">
              {/* Header */}
              <div className="categories-header">
                <h2>
                  <AppstoreOutlined /> Gestion des Catégories
                </h2>
                <p>Organisez vos catégories par branches d'assurance</p>
              </div>

              {/* Statistiques */}
              <div className="stats-card">
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Total Catégories"
                      value={stats.totalCategories}
                      prefix={<AppstoreOutlined />}
                      valueStyle={{ color: "#667eea" }}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Total Branches"
                      value={stats.totalBranches}
                      prefix={<FolderOutlined />}
                      valueStyle={{ color: "#764ba2" }}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Moyenne par branche"
                      value={stats.moyenneParBranche}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Col>
                </Row>
              </div>

              {/* Barre d'actions */}
              <div className="action-bar">
                <Row gutter={16} align="middle">
                  <Col flex="auto">
                    <Search
                      placeholder="Rechercher une catégorie..."
                      allowClear
                      size="large"
                      onSearch={setSearchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{ maxWidth: 400 }}
                    />
                  </Col>
                  <Col>
                    <Space>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                          refreshCategories();
                          refreshBranches();
                        }}
                      >
                        Actualiser
                      </Button>
                      <Button
                        type="default"
                        icon={<PlusOutlined />}
                        onClick={() => handleShowBranche(false)}
                        style={{
                          borderColor: "#764ba2",
                          color: "#764ba2",
                        }}
                      >
                        Nouvelle branche
                      </Button>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => handleShowCategorie(false)}
                        style={{
                          background: "#667eea",
                          borderColor: "#667eea",
                        }}
                      >
                        Nouvelle catégorie
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </div>

              {/* Onglets */}
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  {
                    key: "all",
                    label: (
                      <span>
                        <AppstoreOutlined /> Toutes les catégories
                      </span>
                    ),
                    children: (
                      <Card
                        style={{
                          borderRadius: 12,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        }}
                      >
                        <Table
                          columns={columns}
                          dataSource={categories}
                          rowKey="categorieId"
                          pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `Total: ${total} catégorie(s)`,
                          }}
                        />
                      </Card>
                    ),
                  },
                  {
                    key: "byBranch",
                    label: (
                      <span>
                        <FolderOutlined /> Par branche
                      </span>
                    ),
                    children: (
                      <div>
                        {categoriesByBranche.map((branche) =>
                          renderBrancheCard(branche)
                        )}
                        {categoriesSansBranche.length > 0 && (
                          <Card
                            title={
                              <Space>
                                <FolderOutlined style={{ color: "#999" }} />
                                <span>Sans branche</span>
                                <Badge
                                  count={categoriesSansBranche.length}
                                  style={{ backgroundColor: "#999" }}
                                />
                              </Space>
                            }
                            style={{
                              marginBottom: 16,
                              borderRadius: 12,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            }}
                          >
                            <Table
                              columns={columns}
                              dataSource={categoriesSansBranche}
                              rowKey="categorieId"
                              pagination={false}
                              size="small"
                            />
                          </Card>
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
            <Footer />
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddCategorie
        show={showCategorieModal}
        handleClose={handleCloseCategorie}
        editMode={editMode}
        selectedCategorie={selectedCategorie}
        existingCategories={categories}
        branches={branches}
      />

      <AddBranche
        show={showBrancheModal}
        handleClose={handleCloseBranche}
        editMode={editMode}
        selectedBranche={selectedBranche}
        existingBranches={branches}
      />
    </div>
  );
};

export default Categories;